import { byIdsResultJSONZ } from "_shared/_api/by-ids/by-ids-result";
import { api } from "_shared/_stores/api";
import SearchResults from "_shared/_api/search/search-results";
import Guid from "_shared/guids/guid";
import { AvatarCrop } from "marketplace/_views/avatars/avatar-crop";
import { AvatarSize } from "marketplace/_views/avatars/avatar-size";
import MarketplaceSearchResponseSchema, { MarketplaceSearchInfoJSON } from "marketplace/_schema/marketplace-search-response-schema";
import { marketplaceSearchResponseMiddleware } from "marketplace/_api/marketplace-search-response-middleware";
import MarketplaceCompanyAPIRequestSchema from "marketplace/companies/_api/_schema/marketplace-company-api-request-schema";
import MarketplaceCompanySearchSchema, {
  MarketplaceCompanySearchJSON,
} from "marketplace/companies/_api/_schema/marketplace-company-search-schema";
import { buildUpdateCompanyProfilePayload } from "marketplace/companies/profiles/_api/requests-contracts/update-company-profile-request-schema";
import Company from "marketplace/companies/company";
import CompanySchema, { CompanyJSON } from "marketplace/companies/company-schema";
import CompanyProfile from "marketplace/companies/profiles/company-profile";
import IndividualSchema, { IndividualJSON } from "marketplace/individuals/_schema/individual-schema";
import ForumSchema, { ForumJSON } from "messaging/forums/_schemas/forum-schema";
import z from "zod";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";

const CompaniesByIdsResultJSONZ = byIdsResultJSONZ(CompanySchema.CompanyJSONZ);
export type CompaniesByIdsResultJSON = z.infer<typeof CompaniesByIdsResultJSONZ>;

export const companiesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getCompanyVendors: build.query<CompanyJSON[], void>({
      query: () => "marketplace/company-vendors",
      rawResponseSchema: z.array(CompanySchema.CompanyJSONZ),
      onSchemaFailure: logSchemaFailure,
      providesTags: [{ type: "Company" as const, id: "LIST" }],
    }),

    getCompanyById: build.query<CompanyJSON, { companyId: string }>({
      query: ({ companyId }) => `marketplace/companies/${companyId}`,
      rawResponseSchema: CompanySchema.CompanyJSONZ,
      onSchemaFailure: logSchemaFailure,
      providesTags: (_result, _error, { companyId }) => [{ type: "Company" as const, id: companyId }],
    }),

    /**
     * The companies the viewer may see, and, for every other id asked for, whether it is `missing`
     * (no such company) or `unauthorized` (it exists and the viewer may not see it).
     */
    getCompaniesByIds: build.query<CompaniesByIdsResultJSON, { ids: string[] }>({
      async queryFn({ ids }, _api, _extraOptions, fetchWithBQ) {
        if (!ids.length) return { data: { found: [], unavailable: [] } };
        const result = await fetchWithBQ({
          url: "marketplace/companies-by-ids",
          method: "POST",
          body: ids,
          headers: { "Content-Type": "application/json" },
        });
        if (result.error) return { error: result.error };
        return { data: result.data as CompaniesByIdsResultJSON };
      },
      responseSchema: CompaniesByIdsResultJSONZ,
      onSchemaFailure: logSchemaFailure,
      providesTags: (result) =>
        result
          ? [
              ...result.found.map(({ id }) => ({ type: "Company" as const, id })),
              { type: "Company" as const, id: "LIST" },
            ]
          : [{ type: "Company" as const, id: "LIST" }],
    }),

    /**
     * The company of each legal entity asked for, under the rule `getCompaniesByIds` applies. Each
     * id the answer leaves out is a legal entity id: `unauthorized` when the firm has a company the
     * viewer may not see, `missing` when it has none.
     */
    getCompaniesByLegalEntityIds: build.query<CompaniesByIdsResultJSON, { legalEntityIds: string[] }>({
      query: ({ legalEntityIds }) => ({
        url: "marketplace/companies-by-legal-entity-ids",
        method: "POST",
        body: legalEntityIds,
      }),
      rawResponseSchema: CompaniesByIdsResultJSONZ,
      onSchemaFailure: logSchemaFailure,
      providesTags: (result) =>
        result
          ? [
              ...result.found.map(({ id }) => ({ type: "Company" as const, id })),
              { type: "Company" as const, id: "LIST" },
            ]
          : [{ type: "Company" as const, id: "LIST" }],
    }),

    /**
     * The people at a company that the viewer may see, for a roster that takes a firm's people
     * whole or in part (#6602). A company the viewer may not see is refused with 403, never answered
     * with an empty list; a person the marketplace withholds is left out.
     */
    getCompanyIndividuals: build.query<IndividualJSON[], { companyId: string }>({
      query: ({ companyId }) => `marketplace/companies/${companyId}/individuals`,
      rawResponseSchema: z.array(IndividualSchema.IndividualJSONZ),
      onSchemaFailure: logSchemaFailure,
      providesTags: (result, _error, { companyId }) => [
        { type: "Company" as const, id: companyId },
        ...(result ?? []).map(({ id }) => ({ type: "Individual" as const, id })),
      ],
    }),

    searchCompanies: build.query<SearchResults<MarketplaceSearchInfoJSON>, { search: MarketplaceCompanySearchJSON }>({
      query: ({ search }) => ({
        url: "marketplace/companies",
        params: MarketplaceCompanySearchSchema.toSearchParams(search),
      }),
      rawResponseSchema: MarketplaceSearchResponseSchema.ResponseJSONZ,
      onSchemaFailure: logSchemaFailure,
      transformResponse: marketplaceSearchResponseMiddleware,
      serializeQueryArgs: ({ queryArgs }) => MarketplaceCompanySearchSchema.toCacheKey(queryArgs.search),
      providesTags: [{ type: "Company" as const, id: "LIST" }],
    }),

    getCompanyAvatar: build.query<string, { companyId: string; size?: AvatarSize }>({
      query: ({ companyId, size }) => ({
        url: `marketplace/companies/${companyId}/avatar`,
        params: size ? { size } : undefined,
      }),
      providesTags: (_result, _error, { companyId }) => [{ type: "Company" as const, id: companyId }],
    }),

    createCompany: build.mutation<CompanyJSON, { company: Company }>({
      query: ({ company }) => ({
        url: "marketplace/companies",
        method: "POST",
        body: MarketplaceCompanyAPIRequestSchema.serialize(company),
        headers: { "Content-Type": "application/json" },
      }),
      rawResponseSchema: CompanySchema.CompanyJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: [{ type: "Company" as const, id: "LIST" }],
    }),

    createCompanyAvatar: build.mutation<void, { companyId: string; file: File; crop?: AvatarCrop }>({
      query: ({ companyId, file, crop }) => {
        const formData = new FormData();
        formData.append("file", file);
        if (crop) {
          formData.append("cropX", crop.x.toString());
          formData.append("cropY", crop.y.toString());
          formData.append("cropSize", crop.size.toString());
        }
        return {
          url: `marketplace/companies/${companyId}/avatar`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (_result, _error, { companyId }) => [{ type: "Company" as const, id: companyId }],
    }),

    updateCompany: build.mutation<
      CompanyJSON,
      {
        companyId: string;
        isVisible: boolean;
        contactId?: Guid;
        originalProfile?: CompanyProfile;
        updatedProfile?: CompanyProfile;
      }
    >({
      query: ({ companyId, isVisible, contactId, originalProfile, updatedProfile }) => ({
        url: `marketplace/companies/${companyId}`,
        method: "PATCH",
        body: buildUpdateCompanyProfilePayload(isVisible, contactId, originalProfile, updatedProfile),
        headers: { "Content-Type": "application/json-patch+json" },
      }),
      rawResponseSchema: CompanySchema.CompanyJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error, { companyId }) => [
        { type: "Company" as const, id: companyId },
        { type: "Company" as const, id: "LIST" },
      ],
    }),

    getCompanyContactForum: build.mutation<ForumJSON, { companyId: string }>({
      query: ({ companyId }) => ({
        url: `marketplace/companies/${companyId}/contact`,
        method: "POST",
        body: {},
      }),
      rawResponseSchema: ForumSchema.ForumJSONZ,
      onSchemaFailure: logSchemaFailure,
    }),
  }),
});

export const {
  useGetCompanyVendorsQuery,
  useGetCompanyByIdQuery,
  useGetCompaniesByIdsQuery,
  useGetCompaniesByLegalEntityIdsQuery,
  useGetCompanyIndividualsQuery,
  useLazyGetCompanyIndividualsQuery,
  useSearchCompaniesQuery,
  useGetCompanyAvatarQuery,
  useCreateCompanyMutation,
  useCreateCompanyAvatarMutation,
  useUpdateCompanyMutation,
  useGetCompanyContactForumMutation,
} = companiesApi;
